<template>
  <div>
    <div
      v-if="loading || (type === 'edit' && !editFormDataPopulated)"
      class="row justify-center items-center"
    >
      <q-spinner color="primary" size="3em" />
    </div>
    <div v-else>
      <q-form @submit="performSubmit">
        <FormCard
          title="Inventory Source Details"
          message="Some basic information about your inventory source."
        >
          <template v-slot:inputs>
            <q-input
              filled
              v-model="formData.name"
              label="Inventory Source Name"
              lazy-rules
              :rules="[
                val =>
                  (val && val.length > 1) ||
                  'Enter a name for your inventory source.'
              ]"
              :disable="isLoading"
              hint="Enter a name for your inventory source."
            />

            <q-select
              v-if="advertiserAccountOptions.length > 0"
              filled
              v-model="formData.advertiserAccountId"
              label="Advertiser Account"
              :options="advertiserAccountOptions"
              emit-value
              map-options
              lazy-rules
              :rules="[
                val =>
                  (val && val.length > 0) ||
                  'Select the account this inventory source will be connected to.'
              ]"
              :disable="type !== 'create'"
              hint="Select the account this inventory source will be connected to."
            >
              <template v-slot:selected-item="scope">
                {{ scope.opt.label }}
                <q-badge
                  v-if="
                    scope.opt &&
                      scope.opt.business_id != $store.state.business.record.id
                  "
                  color="gray-light"
                  text-color="gray-medium-dark"
                  :label="'Shared by ' + scope.opt.business_name"
                  style="font-size: 11px"
                  class="q-ml-sm"
                />
              </template>
              <template v-slot:option="scope">
                <q-item dense v-bind="scope.itemProps" class="q-pr-xl">
                  <q-item-section dense>
                    <q-item-label>
                      {{ scope.opt.label }}
                      <q-badge
                        v-if="
                          scope.opt.business_id !=
                            $store.state.business.record.id
                        "
                        color="gray-light"
                        text-color="gray-medium-dark"
                        :label="'Shared by ' + scope.opt.business_name"
                        style="font-size: 11px"
                        class="q-ml-sm"
                      />
                    </q-item-label>
                    <q-item-label caption>{{ scope.opt.value }}</q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </template>
        </FormCard>

        <FormCard title="Choose Your Source Type" class="q-mt-md">
          <template v-slot:message>
            Select the type of inventory source this will this be.
          </template>
          <template v-slot:inputs>
            <q-tooltip v-if="type === 'edit'" anchor="top middle">
              Source type cannot be changed after creation.
            </q-tooltip>
            <SelectionTiles
              v-model="formData.sourceType"
              :options="sourceTypeOptions"
              :disabled="type === 'edit'"
            />
          </template>
        </FormCard>

        <FormCard
          v-if="formData.sourceType === 'feed'"
          title="File Formatting Details"
          class="q-mt-md"
        >
          <template v-slot:message>
            What do the records in your hosted file look like?
            <a
              href="javascript:void(0)"
              @click="promptSourceTypeInfoSidePanel('feed')"
              >Learn More</a
            >
          </template>
          <template v-slot:inputs>
            <SelectionTiles
              v-model="formData.fileFormatType"
              :options="fileFormatTypeOptions"
            />

            <div v-if="formData.fileFormatType == 'custom'">
              <PageBanner
                title="Reminder"
                message="When using Custom Fields, we only map and convert your file's field names. The records must still follow the standardized formatting rules specified in our documentation."
                type="info"
                class="q-mt-md"
                flat
                bordered
              />
            </div>
          </template>
        </FormCard>

        <FormCard
          v-if="formData.sourceType !== 'manual_upload'"
          title="Connection Details"
          message="How should we connect to your hosted file?"
          :has-errors="connectionFailed"
          class="q-mt-md"
        >
          <template v-slot:message>
            How should we connect to your hosted file?
            <a
              href="javascript:void(0)"
              @click="promptSourceTypeInfoSidePanel('feed')"
              >Learn More</a
            >
          </template>
          <template v-if="connectionFailed" v-slot:between>
            <PageBanner
              title="Error"
              message="We were unable to establish a connection to the hosted file. Confirm your connection details and try again."
              type="critical"
              class="q-mt-md"
              flat
              bordered
            />
          </template>

          <template v-slot:inputs>
            <q-select
              filled
              v-model="formData.protocol"
              :options="protocolOptions"
              label="Connection Method (Protocol)"
              emit-value
              map-options
              lazy-rules
              :rules="[
                val =>
                  (val && val.length > 0) ||
                  'Select the protocol your hosted file uses.'
              ]"
              :disable="isLoading"
              hint="Select the protocol your hosted file uses."
            />

            <template v-if="['ftp', 'sftp'].includes(formData.protocol)">
              <q-input
                filled
                v-model="formData.username"
                label="Connection Username"
                lazy-rules
                :rules="[
                  val =>
                    (val && val.length > 0) ||
                    'Enter the username we should use to connect.'
                ]"
                :disable="isLoading"
                hint="Enter the username we should use to connect."
              />
              <q-input
                filled
                type="password"
                v-model="formData.password"
                label="Connection Password"
                lazy-rules
                :rules="[
                  val =>
                    (val && val.length > 0) ||
                    'Enter the password we should use to connect.'
                ]"
                :disable="isLoading"
                hint="Enter the password we should use to connect."
              />
            </template>

            <q-input
              filled
              v-model="formData.fileUrl"
              label="Hosted File URL"
              stack-label
              :placeholder="fileUrlInputPlaceholder"
              :prefix="formData.protocol + '://'"
              lazy-rules
              :rules="[
                val =>
                  (val && val.length > 0) ||
                  'Enter the URL where your file is hosted.'
              ]"
              :disable="isLoading"
              hint="Enter the URL where your file is hosted."
            />

            <q-select
              v-if="formData.sourceType === 'feed'"
              filled
              v-model="formData.isCompressed"
              :options="[
                { label: 'Yes', value: 1 },
                { label: 'No', value: 0 }
              ]"
              label="Is Your Hosted File Compressed? (.zip, .gz, etc)"
              emit-value
              map-options
              lazy-rules
              :rules="[
                val =>
                  val === 1 ||
                  val === 0 ||
                  'If your file is compressed, we\'ll need to know which file to extract.'
              ]"
              :disable="isLoading"
              hint="If your file is compressed, we'll need to know which file to extract."
            />

            <template v-if="formData.isCompressed === 1">
              <q-select
                filled
                v-model="formData.compressedFileType"
                :options="compressedFileTypeOptions"
                label="Compressed File Type"
                emit-value
                map-options
                lazy-rules
                :rules="[
                  val =>
                    (val && val.length > 0) ||
                    'Select the type of compressed file you are hosting.'
                ]"
                :disable="isLoading"
                hint="Select the type of compressed file you are hosting."
              />
            </template>

            <template v-if="formData.isCompressed === 1">
              <q-input
                filled
                v-model="formData.compressedExtractedFilePath"
                label="File Path"
                stack-label
                :placeholder="compressedExtractedFilePathPlaceholder"
                lazy-rules
                :rules="[
                  val =>
                    (val && val.length > 0) ||
                    'Enter the file path for the file we should extract.'
                ]"
                :disable="isLoading"
                hint="Enter the file path for the file we should extract."
              />
            </template>

            <q-select
              filled
              v-model="formData.fileType"
              :options="fileTypeOptions"
              label="File Type"
              emit-value
              map-options
              lazy-rules
              :rules="[
                val => (val && val.length > 0) || 'Select the type of file.'
              ]"
              :disable="isLoading"
              hint="Select the type of file."
            />

            <q-input
              ref="dailyImportTimeInput"
              filled
              v-model="formData.importTime"
              label="Daily Import Time"
              mask="##:## AA"
              :rules="[
                val =>
                  (val && val.length > 0) ||
                  'What time should we import your source\'s ' +
                    $store.state.productTypeConfigs.type +
                    's each day?'
              ]"
              lazy-rules="ondemand"
              :disable="isLoading"
              input-class="cursor-pointer"
              @update:model-value="
                val => {
                  console.log(
                    $dayjs(val)
                      .tz()
                      .utc()
                      .format('HH:mm')
                  );
                }
              "
            >
              <template v-slot:hint>
                What time should we import your source's
                {{ $store.state.productTypeConfigs.type }}s each day? Need more
                frequent imports?
                <a href="javascript:void(0)" @click="openNewContactEmail"
                  >Contact us</a
                >
              </template>
              <template v-slot:append>
                <q-icon name="sym_r_access_time" class="cursor-pointer" />
              </template>
              <q-popup-proxy transition-show="scale" transition-hide="scale">
                <q-time v-model="formData.importTime" mask="hh:mm AA">
                  <div class="row items-center justify-end">
                    <q-btn v-close-popup label="Close" color="primary" flat />
                  </div>
                </q-time>
              </q-popup-proxy>
            </q-input>
          </template>
        </FormCard>
        <FormCard
          v-else
          :title="
            type === 'create'
              ? 'Upload Your Inventory File'
              : 'Upload New Inventory File'
          "
          class="q-mt-md"
        >
          <template v-slot:message>
            Select the file you wish to upload and import inventory from.
            <a
              href="javascript:void(0)"
              @click="promptSourceTypeInfoSidePanel('manual_upload')"
              >Learn More</a
            >
          </template>
          <q-file
            filled
            bottom-slots
            counter
            v-model="formData.manualUploadFile"
            label="Choose a file, or drag & drop one here."
            accept=".csv, .tsv, .psv, .xml"
            max-file-size="52428800"
            hint="File type must be .csv, .tsv, .psv, or .xml. Max file size is 50MB."
            lazy-rules="ondemand"
            :rules="[
              val =>
                val ||
                'File type must be .csv, .tsv, .psv, or .xml. Max file size is 50MB.'
            ]"
            :disable="isLoading"
            @rejected="
              () =>
                triggerActionOutcomeAlert(
                  'upload',
                  false,
                  'File type must be .csv, .tsv, .psv, or .xml. Max file size is 50MB.'
                )
            "
            :autofocus="this.$route.query.manual_upload_initial == 1"
          >
            <template v-slot:prepend>
              <q-icon name="sym_r_upload_file" @click.stop.prevent />
            </template>
            <template v-slot:append>
              <q-icon
                name="sym_r_close"
                @click.stop.prevent="formData.manualUploadFile = null"
                class="cursor-pointer"
              />
            </template>
          </q-file>
        </FormCard>

        <FormCard title="Location & Targeting" class="q-mt-md">
          <template v-slot:message>
            Use location & targeting to control where your inventory is served.
            <PageBanner
              title="Reminder"
              :message="
                'You have the ability to supply these values for each ' +
                  $store.state.productTypeConfigs.type +
                  ' in your inventory source. If no values are supplied for a ' +
                  $store.state.productTypeConfigs.type +
                  ', we will use the default values below when serving it.'
              "
              type="info"
              class="q-mt-md"
              flat
              bordered
            />
          </template>
          <template v-slot:inputs>
            <q-input
              filled
              v-model="formData.defaultPostalCode"
              label="Default Postal Code"
              lazy-rules
              :rules="[
                val =>
                  (val && val.length > 4) ||
                  'Set a default postal code that ' +
                    $store.state.productTypeConfigs.type +
                    's will be served from.'
              ]"
              :disable="isLoading"
              :hint="
                'Set a default postal code that ' +
                  $store.state.productTypeConfigs.type +
                  's will be served from.'
              "
            />
            <q-input
              filled
              v-model="formData.defaultPayableRadius"
              label="Default Targeting Radius (miles)"
              inputmode="numeric"
              mask="#"
              reverse-fill-mask
              :disable="isLoading"
              lazy-rules
              :rules="[
                val =>
                  (val && val >= 10) ||
                  'The minimum allowed targeting radius is 10 miles.'
              ]"
              :hint="
                'How far from the postal code should we target when serving a ' +
                  $store.state.productTypeConfigs.type +
                  '? The minimum allowed targeting radius is 10 miles.'
              "
            />
          </template>
        </FormCard>

        <FormActionsSection>
          <template v-slot:primary-actions>
            <q-btn
              :label="
                customSubmitLabel.length > 0
                  ? customSubmitLabel
                  : type === 'create'
                  ? 'Add Inventory Source'
                  : 'Save Changes'
              "
              type="submit"
              color="primary"
              :disable="isLoading"
              :loading="isLoading"
            />
            <slot name="custom-actions" />
            <q-btn
              v-if="!hideCancel"
              flat
              label="Cancel"
              :to="
                type === 'edit'
                  ? {
                      name: 'AdvertiserAccountsOverview',
                      query: {
                        advertiser_account_id: formData.advertiserAccountId
                      }
                    }
                  : {
                      name: 'AdvertiserAccountsOverview',
                      query: { to_sources: 1 }
                    }
              "
              color="subtle"
              :disable="isLoading"
            />
          </template>
        </FormActionsSection>
      </q-form>
    </div>
  </div>
</template>

<script>
import FormActionsSection from "@/components/UI/FormActionsSection";
import axios from "axios";
import FormCard from "@/components/UI/FormCard";
import SelectionTiles from "@/components/UI/SelectionTiles";
import PageBanner from "@/components/UI/PageBanner.vue";
import * as cronParser from "cron-parser";
import InventorySourcesMixin from "@/mixins/Advertiser/InventorySourcesMixin";

export default {
  name: "AdvertiserInventorySourceForm",
  mixins: [InventorySourcesMixin],
  components: { PageBanner, SelectionTiles, FormCard, FormActionsSection },
  props: {
    type: {
      type: String,
      required: true
    },
    inventorySourceId: {
      type: String,
      required: false
    },
    submitting: {
      type: Boolean,
      required: false,
      default: false
    },
    customSubmitLabel: {
      type: String,
      default: ""
    },
    hideCancel: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      validating: false,
      connectionFailed: false,
      formData: {
        name: "",
        advertiserAccountId: "",
        sourceType: "feed",
        manualUploadFile: null,
        fileFormatType: "standard",
        protocol: "https",
        isCompressed: 0,
        compressedFileType: "",
        compressedExtractedFilePath: "",
        fileType: "csv",
        fileUrl: "",
        importTime: "",
        username: "",
        password: "",
        defaultPostalCode: "",
        defaultPayableRadius: 250
      },
      sourceTypeOptions: [
        {
          icon: "sym_r_language",
          label: "Hosted File",
          value: "feed",
          description:
            "We'll connect to your hosted file and import your " +
            this.$store.state.productTypeConfigs.type +
            "s once a day.",
          descriptionAction: () => this.promptSourceTypeInfoSidePanel("feed"),
          descriptionActionLabel: "Learn More"
        },
        {
          icon: "sym_r_upload_file",
          label: "Manual Upload",
          value: "manual_upload",
          description:
            "You will manually upload your own inventory source file as needed.",
          descriptionAction: () =>
            this.promptSourceTypeInfoSidePanel("manual_upload"),
          descriptionActionLabel: "Learn More"
        }
      ],
      fileFormatTypeOptions: [
        {
          icon: "sym_r_data_check",
          label: "Standardized",
          value: "standard",
          description:
            "The " +
            this.$store.state.productTypeConfigs.type +
            " records have the standardized fields and formatting, as specified in the inventory source documentation."
        },
        {
          iconCustomHtml:
            '<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" style=\'transform: scale(0.65)\'>\n' +
            '<g clip-path="url(#clip0_843_7970)">\n' +
            '<path d="M47.9955 19.1803C47.9947 17.4592 48.0225 15.738 47.8809 14.0186C47.7706 12.6758 47.6151 11.3425 47.2985 10.0305C46.806 7.98971 45.917 6.15263 44.509 4.58134C42.8139 2.68996 40.7328 1.4331 38.2755 0.796856C36.0471 0.220108 33.7692 0.0494281 31.4809 0.021633C29.0497 -0.00833359 26.6181 -0.0044249 24.1869 0.0198958C20.8527 0.0533368 17.5165 -0.105617 14.185 0.141499C13.0593 0.224885 11.937 0.330854 10.827 0.54062C7.89978 1.09392 5.40908 2.42157 3.44084 4.68687C2.19353 6.12223 1.34926 7.76735 0.841126 9.59271C0.248742 11.7212 0.0954347 13.9109 0.0372387 16.1002C-0.0261689 18.4719 0.00640351 20.8471 0.0259469 23.2206C0.0541764 26.6059 -0.0726389 29.9926 0.107161 33.3766C0.171437 34.5836 0.293475 35.784 0.511059 36.9735C0.793353 38.5144 1.24155 40.004 2.03501 41.3608C3.79349 44.3674 6.36584 46.3209 9.73774 47.2125C11.8862 47.7806 14.0851 47.9548 16.293 47.9878C18.8615 48.026 21.4308 47.9964 23.9997 47.9964C25.3095 47.9964 26.6194 47.9995 27.9292 47.9956C30.1563 47.9891 32.3851 48.049 34.6079 47.8284C35.9129 47.699 37.2063 47.517 38.4735 47.1678C40.8318 46.5177 42.8309 45.286 44.4673 43.4697C45.8684 41.915 46.7639 40.0931 47.2694 38.0727C47.8227 35.8617 47.9864 33.606 47.9934 31.3376C48.0055 27.2852 47.9982 23.2327 47.996 19.1803H47.9955ZM43.6083 30.4317C43.5865 32.0942 43.5375 33.7588 43.3477 35.4122C43.1453 37.1742 42.6029 38.8175 41.4732 40.2216C40.3475 41.6214 38.8992 42.5286 37.1706 42.9968C36.0476 43.3008 34.8984 43.4228 33.7401 43.4832C32.0711 43.5709 30.4004 43.587 28.7301 43.6222C25.6474 43.6865 22.5652 43.6556 19.4825 43.6422C18.3038 43.637 17.1247 43.6 15.9469 43.5592C14.4295 43.5067 12.9103 43.4611 11.4176 43.1345C10.1134 42.8491 8.90388 42.3601 7.84854 41.5241C6.54434 40.4913 5.65924 39.1711 5.15024 37.5933C4.7902 36.4767 4.64298 35.3262 4.57175 34.1584C4.43842 31.9695 4.39456 29.7793 4.3976 27.587C4.40107 25.1853 4.39716 22.7837 4.3989 20.382C4.39977 19.4782 4.39108 18.574 4.41671 17.6707C4.45536 16.2978 4.44711 14.922 4.58608 13.5535C4.7116 12.3149 4.84449 11.0715 5.32483 9.90758C6.45791 7.16281 8.51258 5.51595 11.3968 4.88361C12.4169 4.65994 13.4549 4.57612 14.4963 4.52531C16.2813 4.43802 18.0676 4.42369 19.8539 4.38634C22.5456 4.33031 25.2374 4.35507 27.9288 4.36679C29.1652 4.372 30.4025 4.40458 31.6381 4.45061C33.2268 4.51011 34.8207 4.51619 36.3893 4.8341C37.7617 5.11248 39.0451 5.59065 40.1538 6.47314C41.6035 7.62707 42.5295 9.11802 43.0176 10.8952C43.2795 11.8493 43.3772 12.8256 43.4458 13.8089C43.6851 17.2264 43.613 20.6491 43.6139 24.0705C43.6139 26.1907 43.6348 28.3114 43.607 30.4317H43.6083Z"/>\n' +
            '<path d="M31.6033 25.9408L32.5103 19.9687H26.8368V16.0949C26.8368 14.4607 27.6279 12.8668 30.169 12.8668H32.75V7.78243C32.75 7.78243 30.4087 7.37891 28.1712 7.37891C23.4966 7.37891 20.4441 10.2399 20.4441 15.417V19.9687H15.25V25.9408H20.4441V40.3787C21.4869 40.5442 22.5537 40.6289 23.6404 40.6289C24.7272 40.6289 25.7939 40.5442 26.8368 40.3787V25.9408H31.6033Z"/>\n' +
            "</g>\n" +
            "<defs>\n" +
            '<clipPath id="clip0_843_7970">\n' +
            '<rect width="48" height="48.007"/>\n' +
            "</clipPath>\n" +
            "</defs>\n" +
            "</svg>",
          label: "Facebook Vehicle Feed",
          value: "facebook",
          description:
            'Are you already running Automotive Inventory Ads (AIA) on Facebook? Use the vehicle feed from your product catalog. <a href="https://developers.facebook.com/docs/marketing-api/auto-ads/reference#vehicle" target="_blank">See Facebook Reference</a>'
        },
        {
          icon: "sym_r_data_alert",
          label: "Custom Fields",
          value: "custom",
          description:
            "Do your " +
            this.$store.state.productTypeConfigs.type +
            " records have the required data, but use different field names? Leverage our field mapper tool to convert them into the fields that we accept.",
          disabled: true,
          tooltip: "Coming Soon."
        }
      ],
      protocolOptions: [
        // { label: "HTTP", value: "http" },
        { label: "HTTPS", value: "https" },
        { label: "FTP", value: "ftp" },
        { label: "SFTP", value: "sftp" }
      ],
      fileTypeOptions: [
        { label: ".csv", value: "csv" },
        // { label: ".json", value: "json" },
        { label: ".psv", value: "psv" },
        { label: ".tsv", value: "tsv" },
        // { label: ".txt", value: "txt" },
        { label: ".xml", value: "xml" }
      ],
      compressedFileTypeOptions: [
        { label: ".gz", value: "gz" },
        { label: ".tar.gz", value: "tar.gz" },
        { label: ".7z", value: "7z" },
        { label: ".zip", value: "zip" }
      ]
    };
  },
  computed: {
    fileUrlHost() {
      if (this.formData.fileUrl.length > 0) {
        let url = new URL("https://" + this.formData.fileUrl);
        return url.hostname;
      }
      return "";
    },
    fileUrlPath() {
      if (this.formData.fileUrl.length > 0) {
        let url = new URL("https://" + this.formData.fileUrl);
        return url.pathname.replace(/^\/|\/$/g, "");
      }
      return "";
    },
    compressedExtractedFilePathPlaceholder() {
      let fileType =
        this.formData.fileType.length > 0 ? this.formData.fileType : "csv";
      return "example/feeds/feed_file." + fileType;
    },
    fileUrlInputPlaceholder() {
      let fileType =
        this.formData.fileType.length > 0 ? this.formData.fileType : "csv";

      let compressedFileType =
        this.formData.compressedFileType.length > 0
          ? this.formData.compressedFileType
          : "zip";
      if (compressedFileType === "gz") {
        compressedFileType =
          (this.formData.fileType.length > 0 ? this.formData.fileType : "csv") +
          "." +
          compressedFileType;
      }

      let fileUrlPlaceholder =
        "files.example.com/feeds/" +
        (["http", "https"].includes(this.formData.protocol)
          ? "download?id=123"
          : "feed." +
            (this.formData.isCompressed === 1 ? compressedFileType : fileType));

      return fileUrlPlaceholder;
    },
    editFormDataPopulated() {
      return this.formData.name !== "";
    },
    isLoading() {
      return this.submitting || this.validating;
    }
  },
  mounted() {
    this.loading = true;
    this.$store
      .dispatch("advertiserAccounts/GET_REQUEST", {})
      .then(advertiserAccounts => {
        if (advertiserAccounts.length > 0) {
          this.advertiserAccountOptions = this.generateSelectOptionsArray(
            advertiserAccounts,
            "name",
            "id",
            null,
            [
              {
                optionKey: "business_id",
                valueKey: "business_id"
              },
              {
                optionKey: "business_name",
                valueKey: "business_name"
              }
            ]
          );

          if ("advertiser_account_id" in this.$route.query) {
            let validAdvertiserAccountIdParam = advertiserAccounts.find(
              aa => aa.id == this.$route.query.advertiser_account_id
            );
            if (typeof validAdvertiserAccountIdParam !== "undefined") {
              this.formData.advertiserAccountId = this.$route.query.advertiser_account_id;
            }
            this.removeUrlParamsFromWindow(["advertiser_account_id"]);
          }
        }
        this.loading = false;

        if (this.type === "edit" && this.inventorySourceId.length > 0) {
          this.getInventorySource(this.inventorySourceId);
        }
      });
  },
  methods: {
    performSubmit() {
      this.validating = true;
      this.connectionFailed = false;

      let emitData = {
        ...this.formData,
        utcImportTime: this.$dayjs(this.formData.importTime, "hh:mm A")
          .tz()
          .utc()
          .format("HH:mm"),
        host: this.fileUrlHost,
        filePath: this.fileUrlPath,
        isFacebookCatalog: this.formData.fileFormatType === "facebook" ? 1 : 0
      };

      if (this.formData.sourceType === "feed") {
        axios({
          method: "POST",
          url:
            process.env.VUE_APP_API_BASE_URL +
            "/network/app/inventory-sources/verify-connection-details",
          data: {
            host: this.fileUrlHost,
            protocol: this.formData.protocol,
            filename: this.fileUrlPath,
            username: this.formData.username,
            password: this.formData.password
          }
        })
          .then(resp => {
            if (resp.data.data.verified == 1) {
              this.$emit("submit", emitData);
            } else {
              this.connectionFailed = true;
            }
          })
          .catch(e => {
            console.log(e);
            this.connectionFailed = true;
          })
          .finally(() => {
            this.validating = false;
          });
      } else {
        setTimeout(() => {
          this.$emit("submit", emitData);
          this.validating = false;
        }, 500);
      }
    },
    getInventorySource(inventorySourceId) {
      this.$store
        .dispatch("inventorySources/GET_REQUEST", {
          params: {
            id: inventorySourceId
          }
        })
        .then(data => {
          if (data.length > 0) {
            // TODO: Editing sources that have multiple import times (ie, 30 08,22 * * *), will not display correctly and will overwrite with single import time on updates...
            let inventorySource = data[0];

            let importTime = "";
            if (inventorySource.source_type == "feed") {
              let cronSchedule = new cronParser.parseExpression(
                inventorySource.cron_schedule_expression
              );
              importTime = this.convertUtcToBusinessTimezone(
                this.$dayjs(cronSchedule.next().toString()).format(
                  "YYYY-MM-DD HH:mm:ss"
                ),
                "hh:mm A"
              );
            }

            this.formData = {
              name: inventorySource.name,
              advertiserAccountId: inventorySource.advertiser_account_id,
              sourceType: inventorySource.source_type,
              protocol: inventorySource.protocol,
              fileFormatType:
                inventorySource.is_facebook_catalog == 1
                  ? "facebook"
                  : inventorySource.custom_mapping_object
                  ? "custom"
                  : "standard",
              isCompressed: !inventorySource.compressed_filename ? 0 : 1,
              compressedFileType: inventorySource.compressed_file_type,
              compressedExtractedFilePath: inventorySource.compressed_filename,
              fileType: inventorySource.file_type,
              fileUrl:
                inventorySource.host.length > 0 &&
                inventorySource.filename.length > 0
                  ? inventorySource.host + "/" + inventorySource.filename
                  : "",
              importTime: importTime,
              username: inventorySource.username,
              password: inventorySource.password,
              defaultPostalCode: inventorySource.static_postal_code,
              defaultPayableRadius: inventorySource.static_payable_radius
            };
          }
        });
    }
  }
};
</script>

<style scoped lang="scss"></style>
